import CryptoJS from 'crypto-js';
import { ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LOCAL_STORAGE_KEYS } from '../localstorage/LocalStorage.keys';
import { User } from '../models/User';
import { logout } from '../store/actions/auth.actions';

interface IAuthContext {
  user: User | null;
  setUser: (user: User | null) => void;
  clearUser: () => void;
}

interface Props {
  children: ReactNode;
}

const AuthContext = createContext<IAuthContext>({} as IAuthContext);

export const AuthContextProvider = ({ children }: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [user, setUser] = useState<User | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearUser = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.USER);
    dispatch(logout());
    setUser(null);
    navigate('/login');
  }, [navigate, dispatch]);

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (user?.tempo_sessao) {
      //todo remover o 60
      timeoutRef.current = setTimeout(clearUser, Number(user.tempo_sessao) * 60 * 1000 * 60);
    }
  }, [clearUser, user?.tempo_sessao]);

  const handleUserActivity = useCallback(() => {
    resetTimeout();
  }, [resetTimeout]);

  const checkUserLoggedIn = useCallback(() => {
    try {
      const user = localStorage.getItem(LOCAL_STORAGE_KEYS.USER);

      if (user) {
        const secretKey = process.env.REACT_APP_CRYPTO_KEY;
        const decrypted = CryptoJS.AES.decrypt(user, secretKey!).toString(CryptoJS.enc.Utf8);

        const decryptedUserData = JSON.parse(decrypted);

        setUser(decryptedUserData);

        if (decryptedUserData.tempo_sessao) {
          resetTimeout();
        }
        return;
      }
      navigate('/login');
    } catch (error) {
      console.error('Erro ao validar usuário:', error);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.USER);
      navigate('/login');
    }
  }, [navigate, resetTimeout]);

  useEffect(() => {
    checkUserLoggedIn();
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
    };
  }, [checkUserLoggedIn, handleUserActivity]);

  return <AuthContext.Provider value={{ user, setUser, clearUser }}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => useContext(AuthContext);
